import { useEffect, useMemo, useRef, useState } from "react";

import { transferWindowService } from "../../services/api";

// ──────────────────────────────────────────────────────────────────────────
// Centralised transfer-window status logic.
//
// Used by every place that displays a country's transfer-window status —
// Home Quick View, Club/Agent/Staff/Coach Tenders headers, mobile variants.
//
// One round-trip per (country, category) — fetches every season for the
// country (include_past=true) and computes status entirely client-side so
// we can offer four answer states:
//
//   open        — today inside a window     "Open · Closes in N days"
//   soon        — opens in ≤ 14 days        "Opens in N days · Date range"
//   upcoming    — opens in 15-365 days      "Opens in N days · Date range"
//   expected    — all known windows past    "Summer window expected July 2026"
//
// Status is never null when the country exists in the DB; the "expected"
// fallback derives the month from the latest known window of the same
// "type" (label) and adds one year. That's what FIFA tends to repeat
// year-over-year if no new entry is loaded yet.
// ──────────────────────────────────────────────────────────────────────────

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseDate(raw) {
  if (!raw) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(`${raw}T12:00:00Z`);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function formatShortDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function daysBetween(later, earlier) {
  if (!later || !earlier) return 0;
  return Math.max(0, Math.ceil((later.getTime() - earlier.getTime()) / 86400000));
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function windowLabelText(num, raw) {
  const w = (raw || "").toString().trim();
  if (w) return `${capitalize(w)} Window`;
  return `Window ${num}`;
}

// Flatten an entries list into one chronologically-sorted set of windows.
function flattenWindows(entries) {
  const out = [];
  for (const e of entries) {
    const s1 = parseDate(e.window_1_start);
    const e1 = parseDate(e.window_1_end);
    if (s1 && e1) {
      out.push({
        start: s1, end: e1, num: 1,
        label: windowLabelText(1, e.window_1_label),
        season: e.season,
      });
    }
    const s2 = parseDate(e.window_2_start);
    const e2 = parseDate(e.window_2_end);
    if (s2 && e2) {
      out.push({
        start: s2, end: e2, num: 2,
        label: windowLabelText(2, e.window_2_label),
        season: e.season,
      });
    }
  }
  out.sort((a, b) => a.start - b.start);
  return out;
}

// When no future window exists in the DB, predict the next one by taking
// the latest past window of each label (Summer, Winter) and projecting
// it +1 year, then picking the earliest predicted window that is still
// in the future. FIFA windows repeat annually so this is a fair guess;
// we still tag it "expected" so the UI signals it's not authoritative.
//
// The old version just took the chronologically-last window and added
// one year — wrong on May 13, 2026 for Spain (whose 2025/26 Winter was
// the latest past entry), producing "Winter expected January 2027" when
// "Summer expected July 2026" was the actually-next FIFA slot.
function deriveFallback(windowsAsc, now) {
  if (windowsAsc.length === 0) return null;

  // Group by label, keep only the chronologically-latest past window
  // per label. windowsAsc is sorted asc, so overwriting picks the
  // latest of each label.
  //
  // Keys are normalised (lowercase + trimmed) so a CSV import that
  // happens to capitalise one row's label ("Summer" vs "summer") does
  // not split the same FIFA window into two prediction groups and
  // degrade the next-window pick. We keep the original w.label text on
  // the stored value so the display copy preserves the backend's casing.
  const norm = (s) => ((s || "") + "").toLowerCase().trim();
  const latestByLabel = {};
  for (const w of windowsAsc) {
    const key = norm(w.label);
    if (!key) continue;
    latestByLabel[key] = w;
  }

  // Project each label's latest past window forward by one calendar
  // year. Keep only predictions whose start is still in the future.
  const candidates = [];
  for (const w of Object.values(latestByLabel)) {
    const predictedStart = new Date(Date.UTC(
      w.start.getUTCFullYear() + 1,
      w.start.getUTCMonth(),
      w.start.getUTCDate(),
    ));
    if (predictedStart >= now) {
      candidates.push({ label: w.label, start: predictedStart });
    }
  }

  // Pick the earliest future prediction. If every label's +1y is still
  // past (rare; would mean the country hasn't been imported for >1
  // year), fall back to the chronologically-last + 1 year so we never
  // return null here.
  let pick;
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.start - b.start);
    pick = candidates[0];
  } else {
    const last = windowsAsc[windowsAsc.length - 1];
    pick = {
      label: last.label,
      start: new Date(Date.UTC(
        last.start.getUTCFullYear() + 1,
        last.start.getUTCMonth(),
        last.start.getUTCDate(),
      )),
    };
  }

  const monthName = MONTH_NAMES_EN[pick.start.getUTCMonth()];
  const year = pick.start.getUTCFullYear();
  return {
    label: pick.label,          // e.g. "Summer Window"
    monthName,                  // "July"
    year,
    predictedStart: pick.start, // full Date for renderers wanting day-level
    line: `${pick.label} expected ${monthName} ${year}`,
  };
}

export function computeTransferWindowStatus(entries) {
  if (!entries || entries.length === 0) {
    return {
      status: null,
      line: "",
      latestSeason: null,
      windows: [],
      fallback: null,
    };
  }

  // Latest season for context display.
  const sorted = [...entries].sort((a, b) => {
    const ad = parseDate(a.season_start)?.getTime() || 0;
    const bd = parseDate(b.season_start)?.getTime() || 0;
    return bd - ad;
  });
  const latestSeason = sorted[0]?.season || null;

  const windows = flattenWindows(entries);
  const now = new Date();

  // 1) Open right now?
  const open = windows.find((w) => now >= w.start && now <= w.end);
  if (open) {
    const days = daysBetween(open.end, now);
    return {
      status: "open",
      line: `Open · Closes in ${days} day${days === 1 ? "" : "s"}`,
      latestSeason,
      activeWindow: open,
      windows,
      fallback: null,
    };
  }

  // 2) Next future window?
  const next = windows.find((w) => w.start > now);
  if (next) {
    const days = daysBetween(next.start, now);
    return {
      status: days <= 14 ? "soon" : "upcoming",
      line: `Opens in ${days} day${days === 1 ? "" : "s"} · ${formatShortDate(next.start)}`,
      latestSeason,
      activeWindow: null,
      nextWindow: next,
      windows,
      fallback: null,
    };
  }

  // 3) All past — fall back to next-year estimate.
  const fb = deriveFallback(windows, now);
  return {
    status: "expected",
    line: fb ? fb.line : "Next window not yet scheduled",
    latestSeason,
    activeWindow: null,
    nextWindow: null,
    windows,
    fallback: fb,
  };
}

export function useTransferWindowStatus(country, category = "mens_professional") {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!country) {
      setEntries([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await transferWindowService.getAllTransferWindows(
          country, category, 1, 50, null,
          { includePast: true, signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (!res.success) {
          setEntries([]);
          setError(res.error || "Failed to load transfer window");
        } else {
          const list = Array.isArray(res.data?.results)
            ? res.data.results
            : Array.isArray(res.data)
              ? res.data
              : [];
          setEntries(list);
        }
      } catch (e) {
        if (e?.name === "CanceledError") return;
        setEntries([]);
        setError(e?.message || "Network error");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [country, category]);

  const computed = useMemo(() => computeTransferWindowStatus(entries), [entries]);

  return { loading, error, ...computed };
}

export default useTransferWindowStatus;
